// A page with no team of its own (`staticData.teamFrame`, the staff pages) in Pro's or Simple's
// frame: its URL's `?ui=` names the frame, and none is Classic's (spec
// ui-perf/admin-panel-pro-simple). Pro's frame is a feature's (`features/studio2`), which platform
// cannot import, so the composition root gives it (`ProFrameProvider`, in `app/App.tsx`).
import { createContext, useContext, type ReactNode } from "react";
import { z } from "zod";

import type { Ui } from "./places";

/** A `teamFrame` route's search: the frame its pages draw in, Pro's or Simple's, else Classic's. */
export const frameSearch = z.object({
  ui: z.enum(["pro", "simple"]).optional().catch(undefined),
});
export type FrameSearch = z.infer<typeof frameSearch>;

/** The UI a `teamFrame` page's search draws it in. */
export function frameOf(search: unknown): Ui {
  const ui = (search as { ui?: unknown } | undefined)?.ui;
  return ui === "pro" || ui === "simple" ? ui : "classic";
}

export interface ProFrameProps {
  teamId: string;
  brandId: string;
  /** Simple's frame: Pro's header alone, with no nav. */
  simple: boolean;
  children: ReactNode;
}

/** Renders a page inside Pro's frame, or Simple's: the frame's elements, as `renderRoot` does. */
export type ProFrame = (props: ProFrameProps) => ReactNode;

const ProFrameContext = createContext<ProFrame | null>(null);

/** Gives a `teamFrame` page whose `?ui=` names Pro or Simple the frame to draw in. */
export function ProFrameProvider(props: { frame: ProFrame; children: ReactNode }) {
  return <ProFrameContext value={props.frame}>{props.children}</ProFrameContext>;
}

/** Pro's frame, or `null` where the app gave none: such a page then draws in Classic's. */
export function useProFrame(): ProFrame | null {
  return useContext(ProFrameContext);
}
